import React, { useState, useRef, useMemo } from 'react';
import { Plate, Row, SubHeader, Text } from 'features/shared/ui/Page';
import Spacer from 'features/shared/Spacer';
import { Legend } from './styles';
import Table from './Table';
import styled from 'styled-components';
import { BlueButton } from 'features/shared/ui/Button';
import DatePicker from '@gr-monorepo/common/ui/DatePicker';
import { Button } from '@gr-monorepo/common/ui';
import FileSaver from 'file-saver';
import { SchoolsApi } from 'api';
import { useClickStreamContext } from 'features/shared/ClickStream';
import useActivitiesTableData from './useActivitiesTableData';
import useUserData from 'hooks/useUserData';
import { Anchor } from 'features/shared/AnchorScroll';

const TextBlock = styled(Text)``;

interface Props {
  isRegion: boolean;
  abTest: boolean;
}

const Component = ({ isRegion, abTest }: Props) => {
  const { logPageEvent } = useClickStreamContext();
  const textForMunicipality = !isRegion && ' в школе';
  const [pending, setPending] = useState(false);
  const [reportDate, setReportDate] = useState<Date>(new Date());
  const userData = useUserData();
  const activitiesTableData = useActivitiesTableData(userData?.id || 0, isRegion);
  const title = useMemo(
    () => (isRegion ? 'Список муниципалитетов с активными школами' : 'Список активных школ'),
    [isRegion],
  );
  const maxDateReport = new Date();
  const minDateReport = new Date().setDateWithoutTimezone(
    maxDateReport.getTime() > new Date(`${maxDateReport.getFullYear()}-09-01`).getTime()
      ? `${maxDateReport.getFullYear()}-09-01`
      : `${maxDateReport.getFullYear() - 1}-09-01`,
  );
  const startDownload = async () => {
    logPageEvent({
      eventType: 'schools_active_school_click_download_report',
      eventInfo: 'Школы|Активные школы: Клик на скачивание отчета',
    });
    if (pending) return;
    setPending(true);
    const reportDateStr = reportDate.toJSONWithoutTimezone().slice(0, 10);
    try {
      const result = await SchoolsApi.getActivityStatisticsXlsx(reportDateStr);
      const blob = new Blob([result], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      FileSaver.saveAs(blob, `${title} ${reportDateStr}.xlsx`);
    } catch (e) {}
    setPending(false);
  };
  return (
    <Plate>
      <Anchor id="school_active_school" />
      <SubHeader>{isRegion ? 'Список муниципалитетов с активными школами' : 'Список активных школ'}</SubHeader>
      <Spacer size={24} />

      <Row>
        <TextBlock>Настройте дату, чтобы посмотреть статистику за нужный период</TextBlock>
        <Spacer size={24} />
        <Row style={{ height: '50px', alignItems: 'center' }}>
          <DatePicker
            style={{ width: 220 }}
            value={reportDate}
            onChange={(value) => {
              logPageEvent({
                eventType: 'schools_active_school_change_report_date',
                eventInfo: 'Школы|Активные школы: Выбор даты отчета',
              });
              setReportDate(value as Date);
            }}
            format="dd.MM.yyyy"
            disabledСleaning
            maxDate={maxDateReport}
            minDate={minDateReport}
          />
          <Spacer size={24} />
          <Button
            onClick={startDownload}
            color={abTest && activitiesTableData.hasInvolvementLowered ? "red" : undefined}
            style={{ minWidth: 152}}
          >
            {pending ? 'Загрузка' : 'Скачать отчет'}
          </Button>
        </Row>
      </Row>
      <Spacer size={32} />
      <Text>
        В таблице представлены данные по количеству регистраций и активности на платформе среди учителей и&nbsp;учеников
      </Text>
      <Spacer size={32} />
      <Legend>
        <b>Учителя, зарегистрированные с 1 сентября 2025 года</b> — количество учителей, зарегистрированных на платформе
        только в текущем учебном году.
        <Spacer size={8} />
        <b>Ученики, зарегистрированные с 1 сентября 2025 года</b> — количество учеников, зарегистрированных на платформе
        только в текущем учебном году.
        <Spacer size={8} />
        <b>Активные учителя</b> — количество учителей{textForMunicipality} с активными учениками в классе. В статистике
        учитываются педагоги с любой датой регистрации на платформе — не только в текущем учебном году.
        <Spacer size={8} />
        <b>Активные ученики</b> — количество учеников{textForMunicipality}, занимавшихся на Учи.ру в последние 30 дней.
        В статистике учитываются школьники с любой датой регистрации на платформе — не только в текущем учебном году.
      </Legend>
      <Table isRegion={isRegion} abTest={abTest} activitiesTableData={activitiesTableData} />
    </Plate>
  );
};

export default Component;
